import React, {
  createContext,
  useState,
  useEffect,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import axiosClient from "../apis/axiosClient";
import liff from "@line/liff";
import { LINE_LIFF_ID } from "../config/config";
import {useTranslation} from "react-i18next";

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  lineLiffLogin: () => Promise<void>;
  lineLiffLogout: () => void;
  lineProfile: unknown | null;
  lineToken: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(
    !!localStorage.getItem("jwtToken")
  );
  const [lineProfile, setLineProfile] = useState<unknown | null>(
    JSON.parse(localStorage.getItem("lineProfile") || "null")
  );
  const [lineToken, setLineToken] = useState<string | null>(
    localStorage.getItem("lineToken") || null
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Set up Axios interceptors for JWT
  useEffect(() => {
    const reqInterceptor = axiosClient.interceptors.request.use((config) => {
      const token = localStorage.getItem("jwtToken");
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    const resInterceptor = axiosClient.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          logout();
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axiosClient.interceptors.request.eject(reqInterceptor);
      axiosClient.interceptors.response.eject(resInterceptor);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Standard API login
  const login = async (username: string, password: string) => {
    setIsLoading(true);
    try {
      const resp = await axiosClient.post("/login", { username, password });
      const token = resp.data.token;
      localStorage.setItem("jwtToken", token);
      setIsAuthenticated(true);
    } catch (err) {
      console.error("Login failed", err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    const savedLang = localStorage.getItem("i18nextLng");
    localStorage.clear();
    if (savedLang) {
      void i18n.changeLanguage(savedLang);
    }
    setIsAuthenticated(false);
    setLineToken(null);
    setLineProfile(null);
    navigate("/login", { replace: true });
  };

  // LINE LIFF login/logout
  const lineLiffLogin = async () => {
    setIsLoading(true);
    try {
      await liff.init({ liffId: LINE_LIFF_ID });
      if (!liff.isLoggedIn()) {
        liff.login();
      } else {
        const token = await liff.getIDToken();
        const profile = await liff.getProfile();
        localStorage.setItem("lineToken", token ?? "");
        localStorage.setItem("lineProfile", JSON.stringify(profile));
        setLineToken(token);
        setLineProfile(profile);
      }
    } catch (err) {
      console.error("LIFF login error", err);
    } finally {
      setIsLoading(false);
    }
  };

  const lineLiffLogout = () => {
    liff.logout();
    logout();
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        login,
        logout,
        lineLiffLogin,
        lineLiffLogout,
        lineProfile,
        lineToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export { AuthProvider, useAuth, AuthContext };
